import { retrieveLaunchParams } from '@telegram-apps/sdk';

/**
 * Self-hosted crash reporting — the transport-independent core.
 *
 * Crashes caught by the root error boundary are POSTed to our own backend
 * (`POST /api/v1/client-errors`, log-only → Loki) so that visibility does
 * not depend on the Sentry quota, which hard-stopped mid-incident on
 * 2026-08-30 (free plan, 5k errors/month).
 *
 * Everything here is deliberately paranoid about the context it runs in —
 * the app has ALREADY crashed when this code executes:
 *  - no axios / fetcher: its 401 interceptor reloads the page, and the
 *    request pipeline is more machinery than a crash path should need;
 *  - no getConfig(): resolving api_url dynamically imports a config chunk,
 *    and chunk-load failure is itself one of the most common crash causes.
 *    The SPA and /api/v1 are same-origin in every environment (prod, relay,
 *    stage, vite dev proxy), so a RELATIVE url needs no config at all;
 *  - never throws: a crash reporter that crashes is worse than none.
 *
 * This module contains no `import.meta` so ts-jest (CJS) can load it
 * directly — the env wiring lives in crashReport.ts, mirroring the
 * beforeSend.ts / logger.ts split.
 */

/** Mirrors FIELD_LIMITS in server/routes/api/v1/api.v1.client-errors.js. */
const LIMITS = Object.freeze({
  name: 256,
  message: 2000,
  stack: 8192,
  componentStack: 8192,
  release: 128,
  mode: 32,
  path: 512,
  tgWebAppVersion: 32,
  tgWebAppPlatform: 32,
});

/**
 * Byte budget for the serialized payload — measured in BYTES (TextEncoder),
 * because the server's 32KB `max_body_length` and fetch's shared 64KB
 * keepalive budget are byte-enforced while string .length counts UTF-16
 * units. JSON escaping can inflate pathological stacks well beyond their
 * raw length (a control character serializes to 6 bytes), so after
 * re-clamping the size is CHECKED AGAIN and, as the last resort, the stack
 * fields are dropped entirely — a crash report with no stack still beats a
 * 413 that is indistinguishable from success.
 */
const MAX_PAYLOAD_BYTES = 30_000;
const REDUCED_STACK_LIMIT = 4096;

/**
 * Max reports per signature per page load, with the signature keyed on the
 * CLAMPED message (a crash loop whose message interpolates an id would
 * otherwise mint a fresh signature every iteration, defeating the cap and
 * pinning full-length strings in the map). The map itself is bounded: once
 * MAX_TRACKED_SIGNATURES distinct signatures have been seen in one page
 * load, new ones are treated as part of the loop and dropped — that many
 * distinct crash signatures in a single load IS a crash loop. A reload
 * starts a fresh page load and resets everything: intended. The server
 * rate limiter is the backstop behind all of this.
 */
const MAX_REPORTS_PER_SIGNATURE = 3;
const SIGNATURE_MESSAGE_CHARS = 200;
const MAX_TRACKED_SIGNATURES = 100;

export interface CrashReportEnv {
  release: string;
  mode: string;
}

export interface CrashReportPayload {
  message: string;
  name?: string;
  stack?: string;
  componentStack?: string;
  release?: string;
  mode?: string;
  path?: string;
  tgWebAppVersion?: string;
  tgWebAppPlatform?: string;
}

interface ErrorInfoLike {
  componentStack?: string | null;
}

const clamp = (value: unknown, max: number): string | undefined => {
  if (typeof value !== 'string' || value.length === 0) return undefined;
  return value.length > max ? value.slice(0, max) : value;
};

const byteLength = (value: string): number => {
  try {
    return new TextEncoder().encode(value).length;
  } catch {
    // Environments without TextEncoder: UTF-16 length is a lower bound —
    // close enough for a budget check that also has a server-side backstop.
    return value.length;
  }
};

/**
 * The Telegram client version, from the SDK's launch params. NOTE:
 * `window.Telegram.WebApp` does not exist in this app — nothing loads
 * telegram-web-app.js and the @telegram-apps SDK never defines the global —
 * so this is the only working source. Exported for reuse anywhere else that
 * wants the value (e.g. Sentry extras); throws never.
 */
export const getTgWebAppVersion = (): string | undefined => {
  try {
    const { tgWebAppVersion } = retrieveLaunchParams();
    return typeof tgWebAppVersion === 'string' ? tgWebAppVersion : undefined;
  } catch {
    return undefined;
  }
};

/**
 * The Telegram client platform — 'tdesktop' | 'ios' | 'android' | 'macos' |
 * 'web' | 'weba' | … (@telegram-apps/types `Platform`). Same source and same
 * "throws never" contract as getTgWebAppVersion above.
 *
 * WHY IT EXISTS: during the 2026-09-01..04 payment outage we could see that
 * ~40% of launches had no Telegram transport, but not WHICH clients — so
 * "is this a Desktop problem or an everyone problem?" could only be answered
 * by asking the tester what device they were on. Every candidate fix (an
 * in-app QR for СБП, a platform-specific open strategy) is sized by the
 * platform split, and without this field that sizing is guesswork.
 */
export const getTgWebAppPlatform = (): string | undefined => {
  try {
    const { tgWebAppPlatform } = retrieveLaunchParams();
    return typeof tgWebAppPlatform === 'string' ? tgWebAppPlatform : undefined;
  } catch {
    return undefined;
  }
};

export const buildCrashReportPayload = (
  error: unknown,
  errorInfo: ErrorInfoLike | undefined,
  env: CrashReportEnv,
  stackLimit: number = LIMITS.stack,
): CrashReportPayload => {
  const isError = error instanceof Error;
  const message = isError
    ? error.message || 'Unknown error'
    : String(error) || 'Unknown error';

  const payload: CrashReportPayload = {
    message: clamp(message, LIMITS.message) ?? 'Unknown error',
    name: clamp(isError ? error.name : undefined, LIMITS.name),
    // stackLimit caps BOTH stack-ish fields — they are the only fields big
    // enough to matter for the byte budget.
    stack: clamp(isError ? error.stack : undefined, stackLimit),
    componentStack: clamp(errorInfo?.componentStack, stackLimit),
    release: clamp(env.release, LIMITS.release),
    mode: clamp(env.mode, LIMITS.mode),
    // pathname ONLY: hash and query carry Telegram initData (auth material)
    // and must never end up in logs.
    path: clamp(
      typeof window !== 'undefined' ? window.location.pathname : undefined,
      LIMITS.path,
    ),
    tgWebAppVersion: clamp(getTgWebAppVersion(), LIMITS.tgWebAppVersion),
    tgWebAppPlatform: clamp(getTgWebAppPlatform(), LIMITS.tgWebAppPlatform),
  };

  // Drop undefined keys so JSON.stringify produces a minimal body.
  for (const key of Object.keys(payload) as Array<keyof CrashReportPayload>) {
    if (payload[key] === undefined) delete payload[key];
  }

  return payload;
};

export const sendPayload = (payload: CrashReportPayload): void => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return;
  }
  window
    .fetch('/api/v1/client-errors', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      // keepalive lets the request survive an imminent page unload —
      // the user is very likely about to hit "Обновить страницу".
      keepalive: true,
    })
    .catch(() => undefined);
};

export const createCrashReporter = (
  env: CrashReportEnv,
  send: (payload: CrashReportPayload) => void = sendPayload,
) => {
  const seen = new Map<string, number>();

  return (error: unknown, errorInfo?: ErrorInfoLike): void => {
    try {
      const name = error instanceof Error ? error.name : '';
      const message = error instanceof Error ? error.message : String(error);

      // NOTE: beforeSend's SUPPRESSED_EXCEPTIONS (LaunchParamsRetrieveError,
      // UnknownEnvError) are deliberately NOT skipped here.
      //
      // Suppressing them for SENTRY is right — that quota is finite and these
      // fire by design whenever the app is opened outside Telegram. But this
      // channel is a log line on our own backend, so it costs nothing, and
      // skipping them here created a blind spot with real consequences:
      // registration conversion fell from ~31% to ~22% on 2026-08-30 while
      // BOTH error channels reported nothing, because the only errors that
      // could explain it were the two we had muted everywhere. These errors
      // also produce a user-facing dead end ("Откройте приложение в Telegram,
      // или обновите версию"), so they are exactly the ones worth counting.
      //
      // Volume stays bounded by the caps below (<=3 per signature per page
      // load) and by the endpoint's rate limiter.
      const signature = `${name}|${message.slice(0, SIGNATURE_MESSAGE_CHARS)}`;
      const count = seen.get(signature) ?? 0;
      if (count >= MAX_REPORTS_PER_SIGNATURE) {
        return;
      }
      if (count === 0 && seen.size >= MAX_TRACKED_SIGNATURES) {
        return;
      }
      seen.set(signature, count + 1);

      let payload = buildCrashReportPayload(error, errorInfo, env);
      if (byteLength(JSON.stringify(payload)) > MAX_PAYLOAD_BYTES) {
        payload = buildCrashReportPayload(
          error,
          errorInfo,
          env,
          REDUCED_STACK_LIMIT,
        );
        // Escape inflation can beat the reduction (6 bytes per control
        // char) — re-check, and as the last resort ship without stacks.
        if (byteLength(JSON.stringify(payload)) > MAX_PAYLOAD_BYTES) {
          delete payload.stack;
          delete payload.componentStack;
        }
      }

      send(payload);
    } catch {
      // A crash reporter must never take the app down further.
    }
  };
};

/**
 * Probe that reports ONCE per page load if the app was launched from Telegram
 * but no host transport is reachable.
 *
 * Guarding the SDK calls stopped the white screen, but it also removed the
 * only evidence we had of this population: those crashes used to reach the
 * error boundary and land in Loki, and now they are swallowed. Without this,
 * "UnknownEnvError went to zero after the fix" would be true purely by
 * construction — it could not distinguish a recovered transport from 100% of
 * those users sitting silently in degraded mode (no back button, no theme
 * sync). That is the same mistake as muting the errors in the first place.
 *
 * Mirrors the SDK's own postEvent transport checks, in the same order:
 * iframe parent -> TelegramWebviewProxy -> window.external.notify.
 *
 * Gated on launch params being present, because otherwise the series would
 * mix two populations: a plain-browser visitor who was never in Telegram
 * trips every condition here, and that traffic is pre-existing, expected, and
 * already documented as by-design noise in beforeSend's
 * LaunchParamsRetrieveError comment. The gate leaves exactly one meaning:
 * "launched from Telegram, has no transport".
 *
 * TWO KNOWN BLIND SPOTS — this is a floor, not a census:
 *
 *  - It cannot see the Android-teardown class. When TelegramWebviewProxy
 *    .postEvent is still a function but the Java bridge behind it is dead
 *    ("Java object is gone" — the very case that made the catch in safeTma
 *    broad), the probe returns early and counts the session as healthy. It
 *    therefore under-counts by exactly the class that motivated the design.
 *    Detecting it would mean actually calling postEvent, which is a side
 *    effect a diagnostic has no business causing.
 *  - The caller defers it (see crashReport.ts), so sessions that bounce
 *    before the delay elapses are never counted — and bouncing fast is the
 *    abandonment behavior being measured. Deferring is still right (the
 *    client can inject its bridge just after first paint, and a false
 *    positive is worse than a late one), but the series counts "sessions
 *    surviving past the delay", not "affected sessions".
 *
 * One low-cardinality report per affected page load — not per swallowed
 * call — so it stays far inside the endpoint's rate limit.
 *
 * @param report Crash reporter to send through (createCrashReporter's result).
 * @param getLaunchVersion Injectable for tests; defaults to the real lookup.
 */
export const createTransportProbe = (
  report: (error: unknown) => void,
  getLaunchVersion: () => string | undefined = getTgWebAppVersion,
) => {
  let hasReported = false;

  return (): void => {
    try {
      if (hasReported || typeof window === 'undefined') {
        return;
      }
      hasReported = true;

      if (getLaunchVersion() === undefined) {
        return;
      }

      const win = window as typeof window & {
        TelegramWebviewProxy?: { postEvent?: unknown };
        external?: { notify?: unknown };
      };

      const isIframe = window.self !== window.top;
      const hasProxy =
        typeof win.TelegramWebviewProxy?.postEvent === 'function';
      const hasNotify = typeof win.external?.notify === 'function';

      if (isIframe || hasProxy || hasNotify) {
        return;
      }

      const degraded = new Error(
        'Telegram host transport unavailable (degraded mode)',
      );
      degraded.name = 'TmaTransportUnavailable';
      report(degraded);
    } catch {
      // A diagnostic must never affect the app it is measuring.
    }
  };
};

type CaptureContext = {
  tags: Record<string, string>;
  extra: Record<string, unknown>;
};

/**
 * Factory for the router's `defaultOnCatch` (wired in crashReport.ts, used
 * in main.tsx). Extracted so the wiring is TESTABLE — main.tsx is excluded
 * from jest coverage, and without this a silent "reporting stopped
 * entirely" regression would ship green.
 *
 * Reports two ways, each guarded so one failing cannot silence the other:
 *  - our own backend via `report` (quota-independent, → Loki);
 *  - Sentry via `capture` with the REAL error as the primary object.
 *    `extra.error` carries the error object itself so custom properties
 *    (an AxiosError's url/code/status — "which endpoint broke the render")
 *    survive; `tags.section: 'error-boundary'` is what beforeSend's fatal
 *    bypass and the replay sampling gate key on.
 */
export const createOnCatch = (
  report: (error: unknown, errorInfo?: ErrorInfoLike) => void,
  capture: (error: unknown, context: CaptureContext) => unknown,
) => {
  return (error: unknown, errorInfo?: ErrorInfoLike): void => {
    try {
      report(error, errorInfo);
    } catch {
      // never let reporting break the boundary
    }
    try {
      capture(error, {
        tags: { section: 'error-boundary' },
        extra: {
          error,
          componentStack: errorInfo?.componentStack,
          tgWebAppVersion: getTgWebAppVersion(),
        },
      });
    } catch {
      // never let Sentry capture break the boundary
    }
  };
};
